import { lazyFormatPhoneNumber } from "@faire/retailer-visitor-shared/components/PhoneInput/lazyFormatPhoneNumber";
import { usePhoneNumberValidator } from "@faire/retailer-visitor-shared/components/PhoneInput/phoneNumberValidator";
import { setTimeoutIfPossible } from "@faire/retailer-visitor-shared/lib/async-functions/setTimeoutIfPossible";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { logError } from "@faire/web--source/common/logging";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { createToast } from "@faire/web--source/slate/Toast/Toast";
import sendLoggedOutSMS from "@faire/web-api--source/endpoints/www/api/app/send-app-store-sms/post";
import sendLoggedInSMS from "@faire/web-api--source/endpoints/www/api/app/userToken/send-app-store-sms/post";
import { trackRetailerMobileFooterDesktopappModalsendAppLinkClick } from "@faire/web-api--source/events/retailerMobile/click/footerDesktopAppModalSendAppLink";
import React, { useEffect, useMemo, useState } from "react";

import {
  AppDownloadFailureToast,
  AppDownloadSuccessToast,
} from "./AppDowanloadToasts";

export const useAppDownloadState = (
  afterLinkSent?: () => void,
  isLoggedOut?: boolean
) => {
  const { isPending, isInvalid, validatePhoneNumber } =
    usePhoneNumberValidator();

  const { phoneNumber: userStorePhoneNumber, userToken } = useUserStore([
    "phoneNumber",
    "userToken",
  ]);
  const [phoneNumber, setPhoneNumber] = useState(() => {
    const number = userStorePhoneNumber ?? "";
    return number;
  });

  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    debounceIfPossible(() => validatePhoneNumber(phoneNumber), 400, {
      trailing: true,
    })();
  }, [phoneNumber, validatePhoneNumber]);

  const isInvalidPhoneNumber = useMemo(
    () => (submitted ? isInvalid : false),
    [isInvalid, submitted]
  );

  const sendAppLink = useMemo(() => {
    const sendAppLink = async (eventPrefix?: string) => {
      setSubmitted(true);
      if (isPending) {
        setTimeoutIfPossible(() => sendAppLink(eventPrefix), 400);
        return;
      }
      if (isInvalid) {
        if (isLoggedOut) {
          trackRetailerMobileFooterDesktopappModalsendAppLinkClick("failure");
        }
        return;
      }

      try {
        if (isLoggedOut) {
          const linkSent = await sendLoggedOutSMS(phoneNumber);
          if (linkSent) {
            handleSuccess(eventPrefix);
            trackRetailerMobileFooterDesktopappModalsendAppLinkClick("success");
          } else {
            handleFailure(eventPrefix);
            trackRetailerMobileFooterDesktopappModalsendAppLinkClick("failure");
          }
        } else {
          const linkSent = await sendLoggedInSMS(userToken, phoneNumber);
          if (linkSent) {
            trackEvent(`${eventPrefix}/send-app-link-click/success`);
            handleSuccess(eventPrefix);
          } else {
            trackEvent(`${eventPrefix}/send-app-link-click/failure`);
            handleFailure(eventPrefix);
          }
        }
      } catch (err) {
        logError(err);
        handleFailure(eventPrefix);
      } finally {
        afterLinkSent?.();
      }
    };
    const handleSuccess = async (eventPrefix?: string) => {
      const formattedPhoneNumber = await lazyFormatPhoneNumber(phoneNumber);

      createToast(
        <AppDownloadSuccessToast
          formattedPhoneNumber={formattedPhoneNumber}
          onClick={() => sendAppLink(eventPrefix)}
        />,
        { timeout: 8000 }
      );
    };
    const handleFailure = (eventPrefix?: string) => {
      createToast(
        <AppDownloadFailureToast onClick={() => sendAppLink(eventPrefix)} />,
        { timeout: 8000 }
      );
    };
    return sendAppLink;
  }, [
    isPending,
    isInvalid,
    isLoggedOut,
    phoneNumber,
    userToken,
    afterLinkSent,
  ]);

  return {
    phoneNumber,
    setPhoneNumber,
    isInvalidPhoneNumber,
    sendAppLink,
  };
};
